import ArticleIcon from "@mui/icons-material/Article";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PersonIcon from "@mui/icons-material/Person";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Paper,
  styled,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getApplicationDetails } from "../../API/API";

const colors = {
  primary: "#1E293B", 
  secondary: "#7C3AED", 
  gold: "#22D3EE", 
  lightGold: "#E0F2FE", 
  darkBlue: "#0B1324",
  lightBlue: "#EEF2FF",
  text: {
    primary: "#0F172A",
    secondary: "#475569",
    light: "#94A3B8",
  },
  background: {
    paper: "#FFFFFF",
    default: "#F8FAFC",
    highlight: "#F1F5F9",
  },
  divider: "#E2E8F0",
  success: "#16A34A",
  warning: "#F59E0B",
  error: "#DC2626",
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiBackdrop-root": {
    backdropFilter: "blur(6px)",
    backgroundColor: "rgba(2, 6, 23, 0.35)",
  },
  "& .MuiDialog-paper": {
    borderRadius: "16px",
    boxShadow:
      "0 20px 60px rgba(2,6,23,0.25), 0 8px 24px rgba(2,6,23,0.12)",
    overflow: "hidden",
    width: "960px",
    maxWidth: "96vw",
    maxHeight: "92vh",
    margin: "20px",
    padding: 0,
    display: "flex",
    flexDirection: "column",
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.85) 100%)",
    border: `1px solid ${colors.divider}`,
    backdropFilter: "saturate(150%) blur(4px)",
  },
  "& .MuiDialogContent-root": {
    padding: 0,
  },
}));

const ModalContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  padding: "56px 48px",
  width: "100%",
  height: "100%",
  boxSizing: "border-box",
  margin: 0,
  flex: 1,
  background:
    "radial-gradient(1200px 600px at 50% -10%, #EEF2FF 0%, rgba(238,242,255,0) 45%), radial-gradient(900px 500px at 110% 0%, #E0F2FE 0%, rgba(224,242,254,0) 40%), #FFFFFF",
});

const DetailModalContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  padding: "0",
  width: "100%",
  boxSizing: "border-box",
  margin: 0,
  flex: 1,
  overflow: "hidden",
});

const ModalHeader = styled(Box)({
  padding: "28px 40px",
  background:
    "linear-gradient(180deg, rgba(248,250,252,0.9) 0%, rgba(255,255,255,1) 100%)",
  position: "relative",
  borderBottom: `1px solid ${colors.divider}`,
});

const ModalContent = styled(Box)({
  padding: "0",
  flex: 1,
  overflow: "auto",
  background:
    "linear-gradient(180deg, #F8FAFC 0%, #FFFFFF 60%, #F8FAFC 100%)",
});

const TabContentWrapper = styled(Box)({
  padding: "36px 40px",
  flex: 1,
  overflow: "auto",
  backgroundColor: colors.background.paper,
});

const CloseButton = styled(IconButton)({
  position: "absolute",
  right: "16px",
  top: "16px",
  color: colors.text.secondary,
  backgroundColor: "rgba(2,6,23,0.04)",
  border: `1px solid ${colors.divider}`,
  padding: "6px",
  borderRadius: "10px",
  "&:hover": {
    backgroundColor: "rgba(2,6,23,0.08)",
  },
});

const Title = styled(Typography)({
  color: colors.primary,
  fontFamily: "'Playfair Display', serif",
  fontWeight: 800,
  fontSize: "34px",
  letterSpacing: "0.2px",
  lineHeight: "1.2",
  marginTop: "24px",
  marginBottom: "12px",
});

const DetailTitle = styled(Typography)({
  color: colors.primary,
  fontFamily: "'Playfair Display', serif",
  fontWeight: 800,
  fontSize: "26px",
  lineHeight: "1.2",
  marginBottom: "0",
});

const Subtitle = styled(Typography)({
  color: colors.text.secondary,
  fontFamily: "'Inter', sans-serif",
  fontWeight: 600,
  fontSize: "18px",
  lineHeight: "1.5",
  marginBottom: "10px",
});

const Description = styled(Typography)({
  color: colors.text.secondary,
  fontFamily: "'Inter', sans-serif",
  fontWeight: 400,
  fontSize: "15px",
  lineHeight: "1.7",
  marginBottom: "28px",
  maxWidth: "560px",
});

const FieldLabel = styled(Typography)({
  color: colors.text.light,
  fontFamily: "'Inter', sans-serif",
  fontWeight: 600,
  fontSize: "12px",
  marginBottom: "6px",
  textTransform: "uppercase",
  letterSpacing: "0.6px",
});

const FieldValue = styled(Typography)({
  color: colors.text.primary,
  fontFamily: "'Inter', sans-serif",
  fontWeight: 600,
  fontSize: "15px",
  marginBottom: "14px",
});

const SectionTitle = styled(Typography)({
  color: colors.primary,
  fontFamily: "'Inter', sans-serif",
  fontWeight: 700,
  fontSize: "17px",
  marginBottom: "18px",
  marginTop: "6px",
  position: "relative",
  paddingLeft: "14px",
  "&:before": {
    content: '""',
    position: "absolute",
    left: 0,
    top: "2px",
    height: "18px",
    width: "6px",
    background:
      "linear-gradient(180deg, #7C3AED 0%, #22D3EE 100%)",
    borderRadius: "6px",
  },
});

const StyledDocItem = styled(Box)({
  display: "flex",
  alignItems: "center",
  padding: "16px 18px",
  borderRadius: "12px",
  backgroundColor: "#FFFFFF",
  marginBottom: "14px",
  transition: "all 0.2s ease",
  border: `1px solid ${colors.divider}`,
  boxShadow: "0 2px 10px rgba(2,6,23,0.05)",
  "&:hover": {
    boxShadow: "0 10px 24px rgba(2,6,23,0.10)",
    transform: "translateY(-1px)",
    borderColor: colors.secondary,
  },
});

const DocIcon = styled(InsertDriveFileIcon)({
  color: colors.secondary,
  marginRight: "14px",
  fontSize: "22px",
});

const DocInfo = styled(Box)({
  flexGrow: 1,
  overflow: "hidden",
});

const DocTitle = styled(Typography)({
  fontWeight: 600,
  color: colors.text.primary,
  marginBottom: "2px",
  fontSize: "14px",
});

const DocMeta = styled(Typography)({
  color: colors.text.secondary,
  fontSize: "12px",
  marginBottom: "0",
});

const StyledChip = styled(Chip)(({ status }) => {
  let color = colors.success;
  let backgroundColor = "rgba(22, 163, 74, 0.10)";

  if (status === "pending" || status === "incomplete") {
    color = colors.warning;
    backgroundColor = "rgba(245, 158, 11, 0.12)";
  } else if (status === "rejected") {
    color = colors.error;
    backgroundColor = "rgba(220, 38, 38, 0.12)";
  }

  return {
    borderRadius: "9999px",
    fontWeight: 700,
    fontSize: "12px",
    padding: "4px 10px",
    height: "26px",
    color: color,
    backgroundColor: backgroundColor,
    border: `1px solid ${color}20`,
    boxShadow: "0 1px 2px rgba(2,6,23,0.06)",
  };
});

const StyledTabs = styled(Tabs)({
  backgroundColor: "#FFFFFF",
  borderBottom: `1px solid ${colors.divider}`,
  "& .MuiTabs-indicator": {
    background:
      "linear-gradient(90deg, #7C3AED 0%, #22D3EE 100%)",
    height: "3px",
    borderTopLeftRadius: "2px",
    borderTopRightRadius: "2px",
  },
  "& .MuiTab-root": {
    textTransform: "none",
    fontWeight: 600,
    minWidth: "140px",
    padding: "14px 24px",
    color: colors.text.secondary,
    fontSize: "14px",
    transition: "all 0.2s ease",
    opacity: 0.9,
    borderTopLeftRadius: "10px",
    borderTopRightRadius: "10px",
    "&.Mui-selected": {
      color: colors.primary,
      fontWeight: 700,
      opacity: 1,
      backgroundColor: "rgba(124, 58, 237, 0.06)",
    },
    "&:hover": {
      backgroundColor: "rgba(2,6,23,0.04)",
    },
    "& .MuiSvgIcon-root": {
      marginRight: "8px",
      fontSize: "20px",
      color: colors.text.secondary,
    },
    "&.Mui-selected .MuiSvgIcon-root": {
      color: "#7C3AED",
    },
  },
});

const ButtonContainer = styled(Box)({
  display: "flex",
  justifyContent: "center",
  gap: "16px",
  width: "100%",
  marginTop: "16px",
  "@media (max-width: 600px)": {
    flexDirection: "column",
    alignItems: "center",
    gap: "10px",
  },
});

const ResumeButton = styled(Button)({
  border: `1px solid ${colors.divider}`,
  color: colors.primary,
  backgroundColor: "#FFFFFF",
  borderRadius: "12px",
  padding: "12px 24px",
  textTransform: "none",
  fontFamily: "'Inter', sans-serif",
  fontSize: "14px",
  fontWeight: 700,
  minWidth: "220px",
  boxShadow: "0 6px 20px rgba(2,6,23,0.06)",
  "&:hover": {
    backgroundColor: "#F8FAFC",
    borderColor: colors.secondary,
  },
  "@media (max-width: 600px)": {
    width: "100%",
  },
});

const NewApplicationButton = styled(Button)({
  background:
    "linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)",
  color: "white",
  borderRadius: "12px",
  padding: "12px 24px",
  textTransform: "none",
  fontFamily: "'Inter', sans-serif",
  fontSize: "14px",
  fontWeight: 800,
  minWidth: "220px",
  boxShadow: "0 10px 24px rgba(124,58,237,0.25)",
  "&:hover": {
    filter: "brightness(0.98)",
  },
  "@media (max-width: 600px)": {
    width: "100%",
  },
});

const ActionButton = styled(Button)({
  background:
    "linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)",
  color: "white",
  borderRadius: "12px",
  padding: "10px 22px",
  textTransform: "none",
  fontFamily: "'Inter', sans-serif",
  fontSize: "13px",
  fontWeight: 800,
  boxShadow: "0 10px 20px rgba(124,58,237,0.20)",
  "&:hover": {
    filter: "brightness(0.98)",
  },
});

const IllustrationImage = styled("img")({
  width: "280px",
  height: "auto",
  maxWidth: "100%",
  marginBottom: "8px",
  filter: "drop-shadow(0 24px 40px rgba(2,6,23,0.12))",
});

const ContentCard = styled(Paper)({
  borderRadius: "14px",
  boxShadow: "0 8px 24px rgba(2,6,23,0.06)",
  padding: "26px",
  marginBottom: "24px",
  border: `1px solid ${colors.divider}`,
  position: "relative",
  overflow: "hidden",
  background:
    "linear-gradient(180deg, #FFFFFF 0%, rgba(255,255,255,0.95) 100%)",
  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    height: "3px",
    width: "64px",
    background:
      "linear-gradient(90deg, #7C3AED 0%, #22D3EE 100%)",
  },
});

const FooterContainer = styled(Box)({
  padding: "20px 40px",
  borderTop: `1px solid ${colors.divider}`,
  background: "linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)",
});

const StatusContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  marginRight: "20px", 
});

const AppIDLabel = styled(Typography)({
  fontSize: "12px",
  color: colors.text.light,
  fontWeight: 600,
});

const AppIDValue = styled(Typography)({
  fontSize: "15px",
  color: colors.text.primary,
  fontWeight: 700,
});

const formatCurrency = (amount) => {
  if (!amount || amount === 0) return "₹0";

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount);
};

const parseFundRequirement = (value) => {
  if (!value) return "N/A";

  if (value === "1_5L") return "₹1-5 Lakhs";
  if (value === "5_10L") return "₹5-10 Lakhs";
  if (value === "10-50") return "₹10-50 Lakhs";
  if (value === "50-100") return "₹50-100 Lakhs";

  return value;
};

const getEntityTypeLabel = (entityType, borrowerType) => {
  if (entityType === "individual") {
    return "Individual";
  } else if (entityType === "entity") {
    if (borrowerType) {
      const formattedBusinessType =
        borrowerType.charAt(0).toUpperCase() + borrowerType.slice(1);
      return `Entity - ${formattedBusinessType}`;
    }
    return "Entity";
  }
  return "N/A";
};

const getProfessionLabel = (profession) => {
  if (!profession) return "N/A";

  const labels = {
    salaried: "Salaried",
    self_employed: "Self Employed",
    professional: "Professional",
    business: "Business Owner",
  };

  return (
    labels[profession] ||
    profession.replace("_", " ").replace(/\b\w/g, (l) => l.toUpperCase())
  );
};

const getEndUseLabel = (endUse) => {
  if (!endUse) return "N/A";

  const labels = {
    business_expansion: "Business Expansion",
    working_capital: "Working Capital",
    equipment_purchase: "Equipment Purchase",
  };

  return (
    labels[endUse] ||
    endUse.replace("_", " ").replace(/\b\w/g, (l) => l.toUpperCase())
  );
};

const getSecurityOfferedLabel = (security) => {
  if (!security) return "N/A";

  const labels = {
    machinery: "Machinery",
    inventory: "Inventory",
    property: "Property",
  };

  return (
    labels[security] ||
    security.replace("_", " ").replace(/\b\w/g, (l) => l.toUpperCase())
  );
};

const maskSensitiveInfo = (value) => {
  if (!value) return "N/A";
  const length = value.length;
  if (length <= 4) return value;
  return "X".repeat(length - 4) + value.slice(-4);
};

const TabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`loan-details-tabpanel-${index}`}
      aria-labelledby={`loan-details-tab-${index}`}
      {...other}
      style={{ width: "100%", height: "100%" }}
    >
      {value === index && <TabContentWrapper>{children}</TabContentWrapper>}
    </div>
  );
};

const IncompleteLoanModal = ({
  open,
  onClose,
  mode = "incomplete",
  loan = null,
  apiResponse = null,
}) => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [viewingDocument, setViewingDocument] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [applicationDetails, setApplicationDetails] = useState(null);

  useEffect(() => {
    const fetchApplicationData = async () => {
      if (open && mode === "details" && loan) {
        setLoading(true);
        const applicationId = loan.applicationId;
        if (!applicationId) {
          console.error("Missing applicationId for API call:", loan);
          setError("Application ID is required but missing");
          setLoading(false);
          return;
        }
        getApplicationDetails(applicationId)
          .then((response) => {
            if (response.statusCode === 200) {
              setApplicationDetails(
                response?.result?.result ??
                  response?.result ??
                  null
              );
            }
          })
          .catch((error) => {
            console.error("Error fetching application details:", error);
            setError("Failed to load application details");
          })
          .finally(() => {
            setLoading(false);
          });
      }
    };

    fetchApplicationData();

    return () => {};
  }, [open, mode, loan]);

  const handleResumeApplication = () => {
    onClose();
    setTimeout(() => {
      navigate("/resume-application");
    }, 100);
  };

  const handleNewApplication = () => {    
    onClose();
    setTimeout(() => {
      navigate("/apply-loan?new=true");
    }, 100);
  };

  const handleContinueApplication = () => {
    onClose();
    setTimeout(() => {
      if (loan && loan.uuid) {
        navigate(`/apply-loan?resume=${loan.uuid}`);
      } else {
        navigate("/apply-loan?new=true");
      }
    }, 100);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const documentsToShow =
    loan?.documents?.length ? loan.documents : applicationDetails?.documents;

  const viewingDocumentUrl = viewingDocument?.documentPath
    ? String(viewingDocument.documentPath)
    : "";
  const isViewingImage =
    !!viewingDocumentUrl &&
    (String(viewingDocument?.fileType || "")
      .toLowerCase()
      .startsWith("image/") ||
      /\.(png|jpe?g|gif|webp)(\\?|$)/i.test(viewingDocumentUrl));

  const getFormattedDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), "dd MMM yyyy");
    } catch (error) {
      console.error("Error formatting date:", error);
      return "N/A";
    }
  };

  const handleViewDocument = (doc) => {
    setViewingDocument(doc);
    setIsViewDialogOpen(true);
  };
  const handleCloseDocumentView = () => {
    setIsViewDialogOpen(false);
    setViewingDocument(null);
    setError(null);
  };

  // Render incomplete loan notification
  if (mode === "incomplete") {
    const shouldHideNewLoanButton = 
      apiResponse?.message === "You have a pending loan application" &&
      apiResponse?.result === null;

    const handleDialogClose = (_event, reason) => {
      if (reason === "backdropClick" || reason === "escapeKeyDown") {
        return;
      }
      onClose();
    };

    return (
      <StyledDialog
        open={open}
        onClose={handleDialogClose}
        aria-labelledby="incomplete-loan-dialog-title"
        disableEscapeKeyDown
      >
        <ModalContainer>
          <IllustrationImage
            src="/incompleteLoanApplication.png"
            alt="Loan Application Illustration"
          />

          <Title id="incomplete-loan-dialog-title">
            Incomplete Loan Application
          </Title>

          <Subtitle>You have a pending loan application!</Subtitle>

          <Description>
            You've already started a loan application that hasn't been completed
            yet. You can either resume where you left off or start a new
            application.
          </Description>

          <ButtonContainer>
            <ResumeButton variant="outlined" onClick={handleResumeApplication}>
              Resume Application
            </ResumeButton>

            {!shouldHideNewLoanButton && (
              <NewApplicationButton
                variant="contained"
                onClick={handleNewApplication}
              >
                Apply New Loan
              </NewApplicationButton>
            )}
          </ButtonContainer>
        </ModalContainer>
      </StyledDialog>
    );
  }

  // Render loan details view
  return (
    <>
      <StyledDialog
        open={open}
        onClose={onClose}
        aria-labelledby="loan-details-dialog-title"
        fullWidth
        maxWidth="md"
      >
        <DetailModalContainer>
          <ModalHeader>
            <CloseButton onClick={onClose} aria-label="close">
              <CloseIcon fontSize="small" />
            </CloseButton>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <Box>
                <DetailTitle id="loan-details-dialog-title">
                  Loan Application
                </DetailTitle>
                <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                  <AppIDLabel>Application ID:</AppIDLabel>
                  <AppIDValue sx={{ ml: 1 }}>
                    {loan?.applicationNumber || loan?.applicationId || "N/A"}
                  </AppIDValue>
                </Box>
              </Box>

              <StatusContainer>
                <StyledChip
                  label={loan?.status || "Incomplete"}
                  status={loan?.status || "incomplete"}
                />
              </StatusContainer>
            </Box>
          </ModalHeader>

          <StyledTabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="loan details tabs"
          >
            <Tab
              icon={<ArticleIcon />}
              iconPosition="start"
              label="Application"
              sx={{
                borderBottom: tabValue === 0 ? "4px solid transparent" : "none",
              }}
            />
            <Tab
              icon={<PersonIcon />}
              iconPosition="start"
              label="Applicant"
              sx={{
                borderBottom: tabValue === 1 ? "4px solid transparent" : "none",
              }}
            />
            <Tab
              icon={<DescriptionIcon />}
              iconPosition="start"
              label="Documents"
              sx={{
                borderBottom: tabValue === 2 ? "4px solid transparent" : "none",
              }}
            />
          </StyledTabs>

          <ModalContent>
            <TabPanel value={tabValue} index={0}>
              <ContentCard elevation={0}>
                <SectionTitle>Basic Information</SectionTitle>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <FieldLabel>BORROWER TYPE</FieldLabel>
                    <FieldValue>
                      {getEntityTypeLabel(
                        loan?.entityType,
                        loan?.borrowerType
                      ) || "N/A"}
                    </FieldValue>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FieldLabel>FUND REQUIREMENT</FieldLabel>
                    <FieldValue>
                      {parseFundRequirement(loan?.fundRequirement) || "5lac"}
                    </FieldValue>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FieldLabel>SUBMISSION DATE</FieldLabel>
                    <FieldValue>
                      {getFormattedDate(
                        loan?.createDatetime || applicationDetails?.createDatetime
                      ) || "24 Apr 2025"}
                    </FieldValue>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FieldLabel>CURRENT STATUS</FieldLabel>
                    <FieldValue
                      sx={{
                        color:
                          loan?.status === "submitted" ||
                          loan?.status === "Submitted"
                            ? colors.success
                            : loan?.status === "incomplete" ||
                              loan?.status === "Incomplete"
                            ? "#F57C00"
                            : colors.text.primary,
                      }}
                    >
                      {loan?.status
                        ? loan.status.charAt(0).toUpperCase() +
                          loan.status.slice(1)
                        : "Incomplete"}
                    </FieldValue>
                  </Grid>
                </Grid>
              </ContentCard>

              <ContentCard elevation={0}>
                <SectionTitle>Contact Details</SectionTitle>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <FieldLabel>EMAIL ADDRESS</FieldLabel>
                    <FieldValue>
                      {loan?.email || "shubham.sethi@gmail.com"}
                    </FieldValue>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FieldLabel>MOBILE NUMBER</FieldLabel>
                    <FieldValue>{loan?.mobileNo || "9876605141"}</FieldValue>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FieldLabel>PROFESSION</FieldLabel>
                    <FieldValue>
                      {getProfessionLabel(loan?.profession) || "Salaried"}
                    </FieldValue>
                  </Grid>
                </Grid>
              </ContentCard>

              <ContentCard elevation={0}>
                <SectionTitle>Loan Purpose</SectionTitle>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <FieldLabel>End Use of Fund</FieldLabel>
                    <FieldValue
                      style={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {getEndUseLabel(loan?.endUseOfFund)}
                    </FieldValue>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FieldLabel>Security Offered</FieldLabel>
                    <FieldValue>
                      {getSecurityOfferedLabel(loan?.securityOffered)}
                    </FieldValue>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FieldLabel>Security Description</FieldLabel>
                    <FieldValue
                      style={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {loan?.descriptionOfSecurity || "N/A"}
                    </FieldValue>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FieldLabel>Business Description</FieldLabel>
                    <FieldValue
                      style={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {loan?.descriptionOfBusiness || "N/A"}
                    </FieldValue>
                  </Grid>
                </Grid>
              </ContentCard>

              {(loan?.gstin ||
                loan?.udyamRegistrationNo ||
                loan?.panNumber ||
                loan?.totalSalesCurrentYear > 0) && (
                <ContentCard elevation={0}>
                  <SectionTitle>Business Information</SectionTitle>
                  <Grid container spacing={3}>
                    {loan?.gstin && (
                      <Grid item xs={12} sm={6}>
                        <FieldLabel>GSTIN</FieldLabel>
                        <FieldValue>
                          {maskSensitiveInfo(loan?.gstin)}
                        </FieldValue>
                      </Grid>
                    )}
                    {loan?.udyamRegistrationNo && (
                      <Grid item xs={12} sm={6}>
                        <FieldLabel>Udyam Registration</FieldLabel>
                        <FieldValue>
                          {maskSensitiveInfo(loan?.udyamRegistrationNo)}
                        </FieldValue>
                      </Grid>
                    )}
                    {loan?.panNumber && (
                      <Grid item xs={12} sm={6}>
                        <FieldLabel>PAN Number</FieldLabel>
                        <FieldValue>
                          {maskSensitiveInfo(loan?.panNumber)}
                        </FieldValue>
                      </Grid>
                    )}
                    {loan?.totalSalesCurrentYear > 0 && (
                      <Grid item xs={12} sm={6}>
                        <FieldLabel>Total Sales (Current Year)</FieldLabel>
                        <FieldValue>
                          {formatCurrency(loan?.totalSalesCurrentYear)}
                        </FieldValue>
                      </Grid>
                    )}
                  </Grid>
                </ContentCard>
              )}
            </TabPanel>

            {/* Applicant Details Tab */}
            <TabPanel value={tabValue} index={1}>
              {loan?.userId ? (
                <ContentCard elevation={0}>
                  <SectionTitle>User Information</SectionTitle>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <FieldLabel>FULL NAME</FieldLabel>
                      <FieldValue>{loan.userId.name || "N/A"}</FieldValue>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FieldLabel>EMAIL ADDRESS</FieldLabel>
                      <FieldValue>{loan.userId.email || "N/A"}</FieldValue>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FieldLabel>MOBILE NUMBER</FieldLabel>
                      <FieldValue>
                        {loan.userId.mobileNumber || "N/A"}
                      </FieldValue>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FieldLabel>USER TYPE</FieldLabel>
                      <FieldValue>
                        {loan.userId.userType
                          ? loan.userId.userType.charAt(0).toUpperCase() +
                            loan.userId.userType.slice(1)
                          : "N/A"}
                      </FieldValue>
                    </Grid>
                    {loan.userId.gstNumber && (
                      <Grid item xs={12} sm={6}>
                        <FieldLabel>GST NUMBER</FieldLabel>
                        <FieldValue>{loan.userId.gstNumber}</FieldValue>
                      </Grid>
                    )}
                    {loan.userId.address && (
                      <Grid item xs={12}>
                        <FieldLabel>REGISTERED ADDRESS</FieldLabel>
                        <FieldValue>{loan.userId.address}</FieldValue>
                      </Grid>
                    )}
                  </Grid>

                  {loan.userId.profilePhotoUrl && (
                    <Box
                      sx={{
                        mt: 3,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <FieldLabel sx={{ mb: 2, textAlign: "center" }}>
                        PROFILE PHOTO
                      </FieldLabel>
                      <Box
                        sx={{
                          border: `3px solid ${colors.lightGold}`,
                          borderRadius: "50%",
                          padding: "3px",
                          boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                        }}
                      >
                        <img
                          src={loan.userId.profilePhotoUrl}
                          alt="Profile"
                          style={{
                            width: "120px",
                            height: "120px",
                            borderRadius: "50%",
                            objectFit: "cover",
                          }}
                        />
                      </Box>
                    </Box>
                  )}
                </ContentCard>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "300px",
                    backgroundColor: colors.background.highlight,
                    borderRadius: "10px",
                    padding: "32px",
                  }}
                >
                  <PersonIcon
                    sx={{ fontSize: 48, color: colors.text.light, mb: 2 }}
                  />
                  <Typography
                    variant="body1"
                    color={colors.text.secondary}
                    fontWeight={500}
                  >
                    No applicant information available
                  </Typography>
                </Box>
              )}
            </TabPanel>

            {/* Documents Tab */}
            <TabPanel value={tabValue} index={2}>
              {documentsToShow?.length ? (
                <>
                  <Box
                    sx={{
                      mb: 3,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      color={colors.text.primary}
                    >
                      {documentsToShow.length} document
                      {documentsToShow.length > 1 ? "s" : ""} submitted with
                      this application
                    </Typography>
                  </Box>

                  {documentsToShow.map((doc, index) => (
                    <StyledDocItem key={doc.uuid || index}>
                      <DocIcon />
                      <DocInfo>
                        <DocTitle>{doc.documentName}</DocTitle>
                        <DocMeta>Type: {doc.documentType}</DocMeta>
                        <DocMeta>
                          Uploaded: {getFormattedDate(doc.createDatetime)}
                        </DocMeta>
                      </DocInfo>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            handleViewDocument(doc);
                          }}
                          sx={{
                            minWidth: "auto",
                            borderColor: colors.lightGold,
                            color: colors.primary,
                            borderRadius: "4px",
                            fontSize: "13px",
                            padding: "5px 12px",
                            "&:hover": {
                              backgroundColor: "rgba(212, 175, 55, 0.04)",
                              borderColor: colors.secondary,
                            },
                          }}
                        >
                          <VisibilityIcon fontSize="small" sx={{ mr: 0.5 }} />
                          View
                        </Button>
                      </Box>
                    </StyledDocItem>
                  ))}
                </>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "300px",
                    backgroundColor: colors.background.highlight,
                    borderRadius: "10px",
                    padding: "32px",
                  }}
                >
                  <DescriptionIcon
                    sx={{ fontSize: 48, color: colors.text.light, mb: 2 }}
                  />
                  <Typography
                    variant="body1"
                    color={colors.text.secondary}
                    fontWeight={500}
                  >
                    No documents found for this application
                  </Typography>
                </Box>
              )}
            </TabPanel>
          </ModalContent>

          {/* Footer */}
          <FooterContainer>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <Button
                variant="outlined"
                onClick={onClose}
                sx={{
                  borderRadius: "6px",
                  textTransform: "none",
                  borderColor: colors.text.light,
                  color: colors.text.secondary,
                  padding: "8px 20px",
                  fontWeight: 600,
                  "&:hover": {
                    borderColor: colors.text.secondary,
                    backgroundColor: "rgba(90, 90, 90, 0.04)",
                  },
                }}
              >
                Close
              </Button>

              {loan?.status === "incomplete" && (
                <ActionButton onClick={handleContinueApplication}>
                  Continue Application
                </ActionButton>
              )}
            </Box>
          </FooterContainer>
        </DetailModalContainer>
      </StyledDialog>

      {/* Document Details Dialog - Separate from main modal */}
      <Dialog
        open={isViewDialogOpen}
        onClose={handleCloseDocumentView}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            padding: "24px",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogContent>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 4,
              }}
            >
              <CircularProgress color="primary" />
              <Typography sx={{ mt: 2 }}>Loading document...</Typography>
            </Box>
          ) : error ? (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography color="error" gutterBottom>
                {error}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                There was a problem loading the document.
              </Typography>
              <Button
                variant="outlined"
                sx={{ mt: 2 }}
                onClick={() => handleViewDocument(viewingDocument)}
              >
                Retry
              </Button>
            </Box>
          ) : viewingDocument ? (
            <>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography
                  variant="h6"
                  color={colors.primary}
                  fontWeight={700}
                >
                  {viewingDocument.documentName}
                </Typography>
                <Button
                  onClick={handleCloseDocumentView}
                  sx={{
                    color: colors.text.secondary,
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                >
                  <CloseIcon />
                </Button>
              </Box>

              <Box
                sx={{
                  mt: 2,
                  width: "100%",
                  minHeight: "65vh",
                  height: "70vh",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background:
                    "linear-gradient(135deg, #f8fafc 0%, #e9ecef 100%)",
                  borderRadius: "16px",
                  boxShadow: "0 6px 32px 0 rgba(44,62,80,0.10)",
                  border: `1.5px solid #e0e0e0`,
                  p: 0,
                  position: "relative",
                }}
              >
                {viewingDocumentUrl && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                      height: "100%",
                    }}
                  >
                    {isViewingImage ? (
                      <Box
                        component="img"
                        src={viewingDocumentUrl}
                        alt={viewingDocument?.documentName || "Document"}
                        sx={{
                          maxWidth: "96%",
                          maxHeight: "96%",
                          borderRadius: "12px",
                          boxShadow: "0 2px 16px 0 rgba(44,62,80,0.10)",
                          border: "1px solid #e0e0e0",
                          backgroundColor: "#fff",
                          objectFit: "contain",
                        }}
                      />
                    ) : (
                      <iframe
                        src={viewingDocumentUrl}
                        title={viewingDocument?.documentName || "Document"}
                        style={{
                          background: "#fff",
                          borderRadius: "12px",
                          boxShadow: "0 2px 16px 0 rgba(44,62,80,0.10)",
                          border: "1px solid #e0e0e0",
                          width: "96%",
                          height: "96%",
                          display: "block",
                        }}
                        sandbox="allow-same-origin allow-scripts"
                        allowFullScreen={false}
                      />
                    )}
                  </Box>
                )}
              </Box>
            </>
          ) : (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography color={colors.text.secondary}>
                No document available to view.
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default IncompleteLoanModal;
